import React from 'react';
import {ProfileStat} from '../constants';
import {getAssetUrl, getDisplayInitial} from '../../../../utils/assets';
import ProfileEditButton from './ProfileEditButton';
import styles from '../Profile.module.css';

interface ProfileHeaderProps {
  userName: string;
  userEmail?: string;
  stats: ProfileStat[];
  avatarUrl?: string;
  isPro: boolean;
  isEditable?: boolean;
  upgradeLoading?: boolean;
  profilePictureUploading?: boolean;
  onChangeProfilePicture?: () => void;
  onEditProfile?: () => void;
  onUpgradeToPro?: () => void;
}

const iconUrl = (fileName: string) => `${process.env.PUBLIC_URL}/icons/${fileName}`;
const heroIconStyle = (fileName: string) => ({
  '--profile-hero-icon-url': `url("${iconUrl(fileName)}")`,
}) as React.CSSProperties;

const statIconByLabel: Record<string, string> = {
  Followers: 'user-group.svg',
  Following: 'user-fill.svg',
  Listings: 'view-cozy.svg',
};

const CameraIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M8.5 5.5 10 3.8h4l1.5 1.7H19A2.5 2.5 0 0 1 21.5 8v9A2.5 2.5 0 0 1 19 19.5H5A2.5 2.5 0 0 1 2.5 17V8A2.5 2.5 0 0 1 5 5.5h3.5ZM12 16.3a4.3 4.3 0 1 0 0-8.6 4.3 4.3 0 0 0 0 8.6Zm0-2a2.3 2.3 0 1 1 0-4.6 2.3 2.3 0 0 1 0 4.6Z"
      fill="currentColor"
    />
  </svg>
);

const ProfileHeader: React.FC<ProfileHeaderProps> = ({
  userName,
  userEmail,
  stats,
  avatarUrl,
  isPro,
  isEditable = true,
  upgradeLoading = false,
  profilePictureUploading = false,
  onChangeProfilePicture,
  onEditProfile,
  onUpgradeToPro,
}) => {
  const [imageFailed, setImageFailed] = React.useState(false);
  const normalizedAvatarUrl = getAssetUrl(avatarUrl);
  const shouldRenderAvatar = Boolean(normalizedAvatarUrl) && !imageFailed;

  React.useEffect(() => {
    setImageFailed(false);
  }, [normalizedAvatarUrl]);

  const handleShareProfile = async () => {
    const shareData = {
      title: `${userName} on XPSR`,
      text: `View ${userName}'s XPSR profile`,
      url: window.location.href,
    };

    if (typeof navigator.share === 'function') {
      try {
        await navigator.share(shareData);
        return;
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          return;
        }
      }
    }

    if (window.isSecureContext && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(shareData.url);
    }
  };

  return (
    <section className={styles.profileHeader} aria-labelledby="profile-hero-title">
      <div className={styles.profileAvatarColumn}>
        <div className={styles.profileAvatar}>
          {shouldRenderAvatar ? (
            <img
              src={normalizedAvatarUrl}
              alt={`${userName} avatar`}
              className={styles.profileAvatarImage}
              onError={() => setImageFailed(true)}
            />
          ) : (
            getDisplayInitial(userName)
          )}

          {isEditable && onChangeProfilePicture ? (
            <button
              type="button"
              className={styles.profileAvatarCameraButton}
              onClick={onChangeProfilePicture}
              disabled={profilePictureUploading}
              aria-label="Change profile photo"
            >
              <CameraIcon />
            </button>
          ) : null}
        </div>

      </div>

      <div className={styles.profileInfo}>
        <div className={styles.profileIdentityRow}>
          <h1 id="profile-hero-title" className={styles.profileName}>{userName}</h1>
          {isPro ? (
            <img
              src={`${process.env.PUBLIC_URL}/xpsr_pro_badge_light.png`}
              alt="XPSR Pro"
              className={styles.proBadgeImage}
            />
          ) : null}
        </div>

        {userEmail ? (
          <p className={styles.profileEmail}>{userEmail}</p>
        ) : null}

        <div className={styles.profileStats}>
          {stats.map((stat) => (
            <span key={stat.label} className={styles.profileStat}>
              <span
                className={styles.profileStatIcon}
                style={heroIconStyle(statIconByLabel[stat.label] || 'view-cozy.svg')}
                aria-hidden="true"
              />
              <span className={styles.profileStatCopy}>
                <strong>{stat.value}</strong>
                <span>{stat.label}</span>
              </span>
            </span>
          ))}
        </div>

        {isEditable && !isPro && onUpgradeToPro ? (
          <button
            type="button"
            className={styles.upgradeButton}
            onClick={onUpgradeToPro}
            disabled={upgradeLoading}
          >
            {upgradeLoading ? 'Redirecting...' : '★ Upgrade to Pro'}
          </button>
        ) : null}
      </div>

      <div className={styles.profileHeroActions}>
        {isEditable && onEditProfile ? (
          <ProfileEditButton
            className={styles.profileHeroActionPrimary}
            iconClassName={styles.profileHeroActionIcon}
            iconStyle={heroIconStyle('edit-filled.svg')}
            onClick={onEditProfile}
          />
        ) : null}
        <button
          type="button"
          className={styles.profileHeroActionSecondary}
          onClick={() => {
            void handleShareProfile();
          }}
        >
          <span className={styles.profileHeroActionIcon} style={heroIconStyle('share-fill.svg')} aria-hidden="true" />
          Share Profile
        </button>
      </div>
    </section>
  );
};

export default React.memo(ProfileHeader);
